import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { cn } from '../cn';
import { Icon } from './Icon';

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  disabled?: boolean | undefined;
}

export interface SelectProps<V extends string = string> {
  options: readonly SelectOption<V>[];
  value?: V | undefined;
  onChange?: ((value: V) => void) | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  /** Accessible name; required unless `aria-labelledby` is given. */
  'aria-label'?: string | undefined;
  'aria-labelledby'?: string | undefined;
  id?: string | undefined;
  /** Class for the trigger wrapper, for example a width. Default 220 px. */
  className?: string | undefined;
}

/** Single-choice dropdown: a fully rounded trigger, a listbox with rounded options, keyboard navigation. */
export function Select<V extends string = string>({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  className,
  id,
  ...aria
}: SelectProps<V>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const show = () => {
    setActive(
      Math.max(
        0,
        options.findIndex((o) => o.value === value),
      ),
    );
    setOpen(true);
  };
  const pick = (o: SelectOption<V> | undefined) => {
    if (!o || o.disabled) return;
    onChange?.(o.value);
    setOpen(false);
  };
  const move = (delta: number) => {
    setActive((i) => {
      const n = options.length;
      if (!n) return -1;
      let next = i < 0 ? (delta > 0 ? 0 : n - 1) : (i + delta + n) % n;
      for (let k = 0; k < n && options[next]?.disabled; k++) next = (next + delta + n) % n;
      return next;
    });
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) show();
      else move(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) show();
      else pick(options[active]);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Home' && open) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End' && open) {
      e.preventDefault();
      setActive(options.length - 1);
    }
  };

  return (
    <div ref={root} className={cn('relative w-55', className)}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${String(active)}` : undefined}
        onClick={() => {
          if (open) setOpen(false);
          else show();
        }}
        onKeyDown={onKeyDown}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-full border border-border-default px-4 text-left',
          disabled
            ? 'cursor-not-allowed bg-surface-static-disabled'
            : 'cursor-pointer bg-surface-static-default',
        )}
        {...aria}
      >
        <span
          className={cn(
            'ds-body-sm truncate',
            selected ? 'text-content-default' : 'text-content-placeholder',
          )}
        >
          {selected ? selected.label : placeholder}
        </span>
        <Icon name="chevron-down" size={16} color="var(--content-subdued)" />
      </button>
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute top-[110%] right-0 left-0 z-10 m-0 list-none rounded-lg bg-surface-static-default p-1 shadow-4"
        >
          {options.map((o, i) => (
            <li
              key={o.value}
              id={`${listId}-${String(i)}`}
              role="option"
              aria-selected={o.value === value}
              aria-disabled={o.disabled ? true : undefined}
              onMouseEnter={() => {
                setActive(i);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => {
                pick(o);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') pick(o);
              }}
              className={cn(
                'ds-body-sm cursor-pointer rounded-full px-3 py-2 text-content-default',
                o.value === value && 'bg-surface-action-selected-default',
                i === active && o.value !== value && 'bg-surface-static-neutral-hover',
                o.disabled && 'cursor-not-allowed text-content-disabled',
              )}
            >
              {o.label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
